import { makeAutoObservable, runInAction } from 'mobx'
import distributionApi from '../tenancy/objectDistributionApi'
import organizationProfileApi from '../tenancy/organizationProfileApi'
import { normalizeExactDiscoveryEmail, normalizeExactReldoId } from '../tenancy/organizationProfilePolicy'
import { registerTenantScopedReset } from '../tenancy/tenantScopedState'
import organizationSessionStore from './organizationSessionStore'

export class ObjectDistributionStore {
	objectId = null
	distribution = null
	discovery = null
	error = null
	loading = false
	mutationPending = false
	requestVersion = 0
	constructor({ api = distributionApi, discoveryApi = organizationProfileApi, session = organizationSessionStore } = {}) {
		this.api = api; this.discoveryApi = discoveryApi; this.session = session
		makeAutoObservable(this, { api: false, discoveryApi: false, session: false }, { autoBind: true })
	}
	clear() { this.requestVersion += 1; this.objectId = null; this.distribution = null; this.discovery = null; this.error = null; this.loading = false; this.mutationPending = false }
	current(token, objectId) { return token.value === this.requestVersion && this.session.contextGeneration === token.generation && this.objectId === String(objectId) }
	async open(objectId) {
		this.requestVersion += 1
		const token = Object.freeze({ value: this.requestVersion, generation: this.session.contextGeneration })
		this.objectId = String(objectId); this.distribution = null; this.discovery = null; this.error = null; this.loading = true
		try { const distribution = await this.api.get(objectId); if (this.current(token, objectId)) runInAction(() => { this.distribution = distribution; this.loading = false }); return distribution }
		catch (error) { if (this.current(token, objectId)) runInAction(() => { this.error = error; this.loading = false }); return null }
	}
	async discover(kind, value) {
		const token = Object.freeze({ value: this.requestVersion, generation: this.session.contextGeneration }); const objectId = this.objectId
		const normalized = kind === 'email' ? normalizeExactDiscoveryEmail(value) : normalizeExactReldoId(value)
		if (!normalized) { this.discovery = null; return null }
		try { const result = kind === 'email' ? await this.discoveryApi.discoverByEmail(normalized) : await this.discoveryApi.discoverByReldoId(normalized); if (this.current(token, objectId)) runInAction(() => { this.discovery = result }); return result }
		catch (error) { if (this.current(token, objectId)) runInAction(() => { this.error = error; this.discovery = null }); return null }
	}
	async setExposure(exposure) {
		const objectId = this.objectId; const token = Object.freeze({ value: this.requestVersion, generation: this.session.contextGeneration }); if (!objectId || !this.distribution) return null
		this.mutationPending = true
		try { const result = await this.api.setExposure(objectId, exposure); if (this.current(token, objectId)) runInAction(() => { this.distribution = { ...this.distribution, exposure: result.exposure }; this.mutationPending = false }); return result }
		catch (error) { if (this.current(token, objectId)) runInAction(() => { this.error = error; this.mutationPending = false }); return null }
	}
	async replaceShares(shares) {
		const objectId = this.objectId; const token = Object.freeze({ value: this.requestVersion, generation: this.session.contextGeneration }); if (!objectId) return null
		this.mutationPending = true
		try { const result = await this.api.replaceShares(objectId, shares); if (this.current(token, objectId)) runInAction(() => { this.distribution = { ...this.distribution, shares: result.shares }; this.mutationPending = false }); return result }
		catch (error) { if (this.current(token, objectId)) runInAction(() => { this.error = error; this.mutationPending = false }); return null }
	}
}

const objectDistributionStore = new ObjectDistributionStore()
registerTenantScopedReset('object-distribution', () => objectDistributionStore.clear())
export default objectDistributionStore
